import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Users, MapPin, Briefcase, GraduationCap, Wallet, 
  TrendingUp, PieChart, BarChart3, Download, Lock, Eye, 
  Target, Heart, Calendar, Activity, Building, RefreshCw, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, Legend, LineChart, Line
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = 'dse-admin-2026';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [adminKey, setAdminKey] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demographics, setDemographics] = useState(null);
  const [behavior, setBehavior] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [activeTab, setActiveTab] = useState('demographics');

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [demRes, behRes, sentRes, readRes] = await Promise.all([
        axios.get(`${API}/api/investor-data/analytics/demographics?admin_key=${ADMIN_KEY}`),
        axios.get(`${API}/api/investor-data/analytics/investment-behavior?admin_key=${ADMIN_KEY}`),
        axios.get(`${API}/api/investor-data/analytics/market-sentiment?admin_key=${ADMIN_KEY}`),
        axios.get(`${API}/api/investor-data/analytics/financial-readiness?admin_key=${ADMIN_KEY}`)
      ]);
      
      setDemographics(demRes.data);
      setBehavior(behRes.data);
      setSentiment(sentRes.data);
      setReadiness(readRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAuth = () => {
    if (adminKey === ADMIN_KEY) {
      setAuthenticated(true);
      fetchAllData();
    } else {
      toast.error('Invalid admin key');
    }
  };

  const exportData = async (dataType) => {
    try {
      const response = await axios.get(`${API}/api/investor-data/analytics/export?admin_key=${ADMIN_KEY}&data_type=${dataType}`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dse-analytics-${dataType}-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const formatChartData = (data, labelMap = {}) => {
    return data?.map(item => ({
      name: labelMap[item._id] || item._id || 'Unknown',
      value: item.count || item.watch_count || item.buy_interest_count || item.sell_interest_count || 0
    })) || [];
  };

  if (!authenticated) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Admin Access Required
              </CardTitle>
              <CardDescription>
                Enter the admin key to access analytics dashboard
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                type="password"
                placeholder="Enter admin key..."
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
                data-testid="admin-key-input"
              />
              <Button onClick={handleAuth} className="w-full" data-testid="admin-login-btn">
                <Eye className="w-4 h-4 mr-2" />
                Access Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-enter space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <BarChart3 className="w-6 h-6" />
                Investor Analytics Dashboard
              </h1>
              <p className="text-muted-foreground text-sm">
                Tanzania Investor Behavior & Sentiment Data
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchAllData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => exportData('all')}>
              <Download className="w-4 h-4 mr-2" />
              Export All
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">Total Users</span>
                  </div>
                  <p className="text-2xl font-bold">{demographics?.total_users || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Activity className="w-4 h-4" />
                    <span className="text-sm">Profile Completion</span>
                  </div>
                  <p className="text-2xl font-bold">{demographics?.profile_completion_rate || 0}%</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Heart className="w-4 h-4" />
                    <span className="text-sm">Watchlist Entries</span>
                  </div>
                  <p className="text-2xl font-bold">{sentiment?.total_watchlist_entries || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-sm">Youth Rate</span>
                  </div>
                  <p className="text-2xl font-bold">{readiness?.youth_participation?.youth_percentage || 0}%</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="demographics">
                  <Users className="w-4 h-4 mr-1" />
                  Demographics
                </TabsTrigger>
                <TabsTrigger value="behavior">
                  <Target className="w-4 h-4 mr-1" />
                  Behavior
                </TabsTrigger>
                <TabsTrigger value="sentiment">
                  <Heart className="w-4 h-4 mr-1" />
                  Sentiment
                </TabsTrigger>
                <TabsTrigger value="readiness">
                  <Wallet className="w-4 h-4 mr-1" />
                  Readiness
                </TabsTrigger>
              </TabsList>

              {/* Demographics Tab */}
              <TabsContent value="demographics" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Region Distribution */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Regional Distribution
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={formatChartData(demographics?.region_distribution?.slice(0, 10))} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#10b981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Income Distribution */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Wallet className="w-4 h-4" />
                        Income Distribution
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPie>
                            <Pie
                              data={formatChartData(demographics?.income_distribution)}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                              label={({ name, percent }) => `${name.slice(0, 10)}... ${(percent * 100).toFixed(0)}%`}
                            >
                              {formatChartData(demographics?.income_distribution).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </RechartsPie>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Education Distribution */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <GraduationCap className="w-4 h-4" />
                        Education Level
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={formatChartData(demographics?.education_distribution)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#3b82f6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Employment Distribution */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Employment Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPie>
                            <Pie
                              data={formatChartData(demographics?.employment_distribution)}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {formatChartData(demographics?.employment_distribution).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </RechartsPie>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Behavior Tab */}
              <TabsContent value="behavior" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Investment Horizons */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Investment Horizons
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={formatChartData(behavior?.investment_horizons)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#10b981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Preferred Assets */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <PieChart className="w-4 h-4" />
                        Preferred Asset Types
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={formatChartData(behavior?.preferred_assets)} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#8b5cf6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Investment Goals */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Investment Goals
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={formatChartData(behavior?.investment_goals)} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#f59e0b" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Account Penetration */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        Account Penetration
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between items-center p-4 bg-muted rounded-lg">
                        <span>Broker Account Rate</span>
                        <Badge variant="secondary" className="text-lg">{behavior?.broker_account_rate || 0}%</Badge>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-muted rounded-lg">
                        <span>CDS Account Rate</span>
                        <Badge variant="secondary" className="text-lg">{behavior?.cds_account_rate || 0}%</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        CDS (Central Depository System) is required to trade on DSE
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Sentiment Tab */}
              <TabsContent value="sentiment" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Most Watched Stocks */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Most Watched Stocks
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={formatChartData(sentiment?.most_watched_stocks)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#10b981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Buy vs Sell Interest */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Buy vs Sell Interest
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-green-600 mb-2">Top Buy Interest</h4>
                          <div className="flex flex-wrap gap-2">
                            {sentiment?.buy_interest?.slice(0, 5).map((item, idx) => (
                              <Badge key={idx} className="bg-green-100 text-green-700">
                                {item._id}: {item.buy_interest_count}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-red-600 mb-2">Top Sell Interest</h4>
                          <div className="flex flex-wrap gap-2">
                            {sentiment?.sell_interest?.slice(0, 5).map((item, idx) => (
                              <Badge key={idx} className="bg-red-100 text-red-700">
                                {item._id}: {item.sell_interest_count}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Summary Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sentiment Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <p className="text-2xl font-bold text-primary">{sentiment?.total_watchlist_entries || 0}</p>
                        <p className="text-sm text-muted-foreground">Watchlist Entries</p>
                      </div>
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{sentiment?.total_sentiment_signals || 0}</p>
                        <p className="text-sm text-muted-foreground">Sentiment Signals</p>
                      </div>
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{sentiment?.total_market_reactions || 0}</p>
                        <p className="text-sm text-muted-foreground">Market Reactions</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Financial Readiness Tab */}
              <TabsContent value="readiness" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Savings Capacity */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Wallet className="w-4 h-4" />
                        Monthly Savings Capacity
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={formatChartData(readiness?.savings_capacity_distribution)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#10b981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Youth Participation */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Youth Participation (Under 35)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-6xl font-bold text-primary mb-2">
                            {readiness?.youth_participation?.youth_percentage || 0}%
                          </div>
                          <p className="text-muted-foreground">
                            {readiness?.youth_participation?.youth_count || 0} youth investors out of {
                              (readiness?.youth_participation?.youth_count || 0) + (readiness?.youth_participation?.older_count || 0)
                            } with age data
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Existing Investments */}
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        Existing Investment Holdings
                      </CardTitle>
                      <CardDescription>
                        What types of investments do users currently own?
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={formatChartData(readiness?.existing_investments)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#8b5cf6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </Layout>
  );
}
